import { markRaw } from 'vue'
import { defineStore } from 'pinia'
import { TradeDirection } from '@injectivelabs/sdk-ts/types/light'
import { getIndexerDerivativesApi } from '@shared/service/derivative'
import {
  setPendingCloseMarketId,
  clearPendingCloseMarketId,
  clearPendingCloseMarketIds,
  reconcilePendingCloseMarketIds,
  schedulePendingCloseMarketIdExpiry
} from './pending'
import {
  hasOptimisticRfqExecution,
  applyOptimisticRfqExecution,
  clearOptimisticRfqExecution,
  clearOptimisticRfqExecutions,
  rollbackOptimisticRfqExecution,
  type OptimisticRfqExecutionMergeOptions,
  mergePositionsWithOptimisticRfqExecutions
} from './optimisticRfq'
import { RFQ_CONTRACT_ADDRESS } from '@/app/data/rfq'
import { DEFAULT_TABLE_ITEMS_AMOUNT } from '@/app/utils/constants'
import {
  getChainGrpcWasmApi,
  getIndexerGrpcRfqApi,
  getIndexerGrpcTcDerivativesApi
} from '@/app/Services'
import {
  createRequestKey,
  CLIENT_REQUEST_KEYS,
  apiCallsRequestCache
} from '@/app/services/apiCallsRequestCache'
import { TpSlTriggerType } from '@/types/rfq'
import type {
  Orderbook,
  RFQConditionalOrder,
  TcDerivativePosition
} from '@injectivelabs/sdk-ts'
import type { TakerIntentState } from '@/types/rfq'

type OrderBookMap = Record<string, Orderbook>
type TpSlOrdersMap = Record<string, RFQConditionalOrder[]>
type IntentLaneMap = Record<string, TakerIntentState>

const TP_SL_POSITION_FETCH_ATTEMPTS = 5
const TP_SL_POSITION_FETCH_DELAY_MS = 1500
const importPositionStream = () => import('./stream')
const importPositionMessage = () => import('./message')

type PositionStoreState = {
  orderbooks: OrderBookMap
  tpSlOrders: TpSlOrdersMap
  intentLanes: IntentLaneMap
  suppressedMarketIds: Set<string>
  positions: TcDerivativePosition[]
  pendingCloseMarketIds: Set<string>
  optimisticRfqExecutionMarketIds: Set<string>
}

const initialStateFactory = (): PositionStoreState => ({
  positions: markRaw([] as TcDerivativePosition[]),
  orderbooks: {} as OrderBookMap,
  tpSlOrders: {} as TpSlOrdersMap,
  intentLanes: {} as IntentLaneMap,
  suppressedMarketIds: new Set<string>(),
  pendingCloseMarketIds: new Set<string>(),
  optimisticRfqExecutionMarketIds: new Set<string>()
})

export const usePositionStore = defineStore('position', {
  state: (): PositionStoreState => initialStateFactory(),
  getters: {
    positionsBySubaccountId: (state) => {
      return (subaccountId: string) => {
        const derivativeStore = useDerivativeStore()

        return state.positions.filter(
          (position) =>
            position.subaccountId === subaccountId &&
            derivativeStore.activeMarketIds.includes(position.marketId)
        )
      }
    },

    subaccountPositions: (state) => {
      const accountStore = useAccountStore()
      const derivativeStore = useDerivativeStore()

      return state.positions.filter(
        ({ marketId, subaccountId }) =>
          subaccountId === accountStore.subaccountId &&
          derivativeStore.activeMarketIds.includes(marketId)
      )
    },

    findTpSlPosition: (state) => {
      return (marketId: string) => {
        const accountStore = useAccountStore()
        const derivativeStore = useDerivativeStore()

        const position = state.positions.find(
          (position) =>
            position.marketId === marketId &&
            position.subaccountId === accountStore.subaccountId &&
            derivativeStore.activeMarketIds.includes(position.marketId)
        )

        return position
      }
    },

    tpSlByMarketId: (state) => {
      return (marketId: string, positionDirection: TradeDirection) => {
        const orders = state.tpSlOrders[marketId] || []

        // TP/SL trigger direction depends on the position's direction:
        //   LONG  → TP = mark_price_gte, SL = mark_price_lte
        //   SHORT → TP = mark_price_lte, SL = mark_price_gte
        const isLong = positionDirection === TradeDirection.Long

        const tpTrigger = isLong
          ? TpSlTriggerType.MarkPriceGte
          : TpSlTriggerType.MarkPriceLte

        const slTrigger = isLong
          ? TpSlTriggerType.MarkPriceLte
          : TpSlTriggerType.MarkPriceGte

        const tpOrder = orders.find((order) => order.triggerType === tpTrigger)
        const slOrder = orders.find((order) => order.triggerType === slTrigger)

        return { tpOrder, slOrder }
      }
    }
  },
  actions: {
    // synchronous actions doesn't need to be lazy loaded
    setPendingCloseMarketId,
    clearPendingCloseMarketId,
    clearPendingCloseMarketIds,
    reconcilePendingCloseMarketIds,
    applyOptimisticRfqExecution,
    clearOptimisticRfqExecution,
    clearOptimisticRfqExecutions,
    hasOptimisticRfqExecution,
    rollbackOptimisticRfqExecution,
    schedulePendingCloseMarketIdExpiry,

    setPositions(
      positions: TcDerivativePosition[],
      options?: OptimisticRfqExecutionMergeOptions
    ) {
      this.positions = markRaw(
        mergePositionsWithOptimisticRfqExecutions(positions, options)
      )
    },

    setOrderbooks(orderbooks: OrderBookMap) {
      this.orderbooks = orderbooks
    },

    setTpSlOrders(tpSlOrders: TpSlOrdersMap) {
      this.tpSlOrders = tpSlOrders
    },

    setTpSlOrdersByMarketId({
      marketId,
      orders
    }: {
      marketId: string
      orders: RFQConditionalOrder[]
    }) {
      const positionStore = usePositionStore()

      positionStore.setTpSlOrders({
        ...positionStore.tpSlOrders,
        [marketId]: orders
      })
    },

    setIntentLanes(intentLanes: IntentLaneMap) {
      this.intentLanes = intentLanes
    },

    setIntentLane({
      marketId,
      intentLane
    }: {
      marketId: string
      intentLane: TakerIntentState
    }) {
      const positionStore = usePositionStore()

      positionStore.setIntentLanes({
        ...positionStore.intentLanes,
        [marketId]: intentLane
      })
    },

    cancelTp: lazyPiniaAction(importPositionMessage, 'cancelTp'),
    cancelSl: lazyPiniaAction(importPositionMessage, 'cancelSl'),
    applyTpSl: lazyPiniaAction(importPositionMessage, 'applyTpSl'),
    cancelAllTpSl: lazyPiniaAction(importPositionMessage, 'cancelAllTpSl'),
    addMarginToPosition: lazyPiniaAction(
      importPositionMessage,
      'addMarginToPosition'
    ),
    closeLimitPosition: lazyPiniaAction(
      importPositionMessage,
      'closeLimitPosition'
    ),
    closeMarketPosition: lazyPiniaAction(
      importPositionMessage,
      'closeMarketPosition'
    ),
    streamTcDerivativePositions: lazyPiniaAction(
      importPositionStream,
      'streamTcDerivativePositions'
    ),
    cancelTcDerivativePositionsStream: lazyPiniaAction(
      importPositionStream,
      'cancelTcDerivativePositionsStream'
    ),

    async fetchIntentLane(marketId: string) {
      const positionStore = usePositionStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected || !marketId) {
        return
      }

      const wasmApi = await getChainGrpcWasmApi()

      const response = await wasmApi.fetchSmartContractState(
        RFQ_CONTRACT_ADDRESS,
        {
          taker_intent_state: {
            taker: sharedWalletStore.injectiveAddress,
            market_id: marketId,
            subaccount_nonce: 0
          }
        }
      )

      const data = JSON.parse(new TextDecoder().decode(response.data))

      const intentState: TakerIntentState = {
        epoch: Number(data?.epoch ?? 0),
        laneVersion: Number(data?.lane_version ?? 0)
      }

      positionStore.setIntentLane({ marketId, intentLane: intentState })

      return intentState
    },

    async fetchTpSl() {
      const positionStore = usePositionStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        positionStore.setTpSlOrders({})

        return
      }

      const indexerGrpcRfqApi = await getIndexerGrpcRfqApi()

      const response = await indexerGrpcRfqApi.listConditionalOrders({
        requestAddress: sharedWalletStore.injectiveAddress
      })

      const orders = response.orders

      const liveOrders = (orders || []).filter(
        (order) => order.status === 'pending_trigger'
      )

      const liveOrdersByMarketId = liveOrders.reduce((map, order) => {
        if (!map[order.marketId]) {
          map[order.marketId] = []
        }

        map[order.marketId]!.push(order)

        return map
      }, {} as TpSlOrdersMap)

      positionStore.setTpSlOrders(liveOrdersByMarketId)
    },

    async fetchTcDerivativePositions({
      token,
      limit,
      marketId,
      direction,
      shouldReconcileOptimisticRfqExecutions
    }: {
      limit?: number
      token?: string
      marketId?: string
      direction?: string
      shouldReconcileOptimisticRfqExecutions?: boolean
    }) {
      const positionStore = usePositionStore()
      const subaccountStore = useSubaccountStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      const perPage = limit || DEFAULT_TABLE_ITEMS_AMOUNT
      const shouldReconcilePendingClose = !token && !limit && !direction
      const indexerGrpcTcDerivativesApi = await getIndexerGrpcTcDerivativesApi()
      const { positions, next } = await apiCallsRequestCache.dedupe(
        createRequestKey(CLIENT_REQUEST_KEYS.tcDerivativePositions, {
          token,
          perPage,
          marketId,
          direction,
          accountAddress: sharedWalletStore.injectiveAddress
        }),
        () =>
          indexerGrpcTcDerivativesApi.fetchPositions({
            token,
            perPage,
            marketId,
            direction,
            withCount: true,
            accountAddress: sharedWalletStore.injectiveAddress
          })
      )

      if (!token && !marketId) {
        subaccountStore.setPositionsCursors([])
      }

      const uniqueNewCursors =
        next.filter(
          (cursor) => !subaccountStore.positionsCursors.includes(cursor)
        ) ?? []

      const positionsForStore = marketId
        ? [
            ...positions,
            ...positionStore.positions.filter(
              (position) => position.marketId !== marketId
            )
          ]
        : positions

      positionStore.setPositions(
        positionsForStore,
        shouldReconcileOptimisticRfqExecutions
          ? {
              confirmedMarketIds: marketId ? [marketId] : undefined,
              isCompleteSnapshot: !marketId && next.length === 0
            }
          : undefined
      )

      if (shouldReconcilePendingClose && (marketId || next.length === 0)) {
        positionStore.reconcilePendingCloseMarketIds({
          positions,
          marketId
        })
      }

      if (!marketId) {
        subaccountStore.setPositionsCursors([
          ...subaccountStore.positionsCursors,
          ...uniqueNewCursors
        ])
      }
    },

    async fetchTpSlPosition(marketId: string) {
      const positionStore = usePositionStore()

      for (
        let attempt = 0;
        attempt < TP_SL_POSITION_FETCH_ATTEMPTS;
        attempt += 1
      ) {
        await positionStore.fetchTcDerivativePositions({})

        const position = positionStore.findTpSlPosition(marketId)

        if (position) {
          return position
        }

        if (attempt < TP_SL_POSITION_FETCH_ATTEMPTS - 1) {
          await sleep(TP_SL_POSITION_FETCH_DELAY_MS)
        }
      }

      return undefined
    },

    // Todo: @ivan verify if we still needs this
    // Fetching multiple market orderbooks for unrealized PnL calculation within a market page
    async fetchOpenPositionsMarketsOrderbook() {
      const indexerDerivativesApi = await getIndexerDerivativesApi()

      const accountStore = useAccountStore()
      const positionStore = usePositionStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected || !accountStore.subaccountId) {
        return
      }

      if (positionStore.subaccountPositions.length === 0) {
        return
      }

      const marketsOrderbook = await indexerDerivativesApi.fetchOrderbooksV2(
        positionStore.subaccountPositions.map((position) => position.marketId)
      )
      const marketsOrderbookMap = marketsOrderbook.reduce(
        (marketOrderbooks, { orderbook }, index) => {
          const position = positionStore.subaccountPositions[index]
          if (!position) {
            return marketOrderbooks
          }

          return {
            ...marketOrderbooks,
            [position.marketId]: orderbook
          }
        },
        {} as OrderBookMap
      )

      positionStore.setOrderbooks(marketsOrderbookMap)
    }
  }
})
